/**
 * TestVehicleCleanupCard (Jul-2026, User-Wunsch) — Admin-Werkzeug:
 * Test-/Demo-Fahrzeuge bereinigen. Vorschau (Dry-Run) + gezielte Löschung.
 * Importierter Bestand (source=import) und kundenverbundene Fahrzeuge sind
 * server-seitig hart geschützt und werden nie gelöscht.
 */
import { useState } from "react";
import { Trash2, Loader2, Eraser, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

export const TestVehicleCleanupCard = () => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [checked, setChecked] = useState({});

    const loadPreview = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/test-vehicle-cleanup");
            setPreview(data);
            const init = {};
            (data.dealer_candidates || []).forEach((c) => { init[c.id] = true; });
            setChecked(init);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    };

    const run = async () => {
        const extra = Object.keys(checked).filter((id) => checked[id]);
        const total = (preview?.demo_total || 0) + extra.length;
        if (!window.confirm(`${total} Fahrzeug(e) endgültig löschen?\n— ${preview?.demo_total || 0} von Demo-/Systemkonten\n— ${extra.length} bestätigte Test-Inserate echter Händler\n\nImportierter Bestand & kundenverbundene Fahrzeuge bleiben geschützt.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/test-vehicle-cleanup", { delete_demo: true, extra_listing_ids: extra });
            toast.success(`Bereinigt: ${data.deleted_demo} Demo- + ${data.deleted_dealer} Test-Fahrzeuge gelöscht`);
            setPreview(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bereinigung fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border-2 border-red-200 rounded-sm p-4" data-testid="test-vehicle-cleanup-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <Eraser className="w-5 h-5 text-red-600" /> Test-/Demo-Fahrzeuge bereinigen
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        Importierter Bestand (source=import) und kundenverbundene Fahrzeuge sind hart geschützt.
                    </p>
                </div>
                <Button onClick={loadPreview} disabled={loading} variant="outline" className="rounded-sm border-gray-300" data-testid="cleanup-preview-btn">
                    {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Vorschau laden
                </Button>
            </div>
            {preview && (
                <div className="mt-3 space-y-3">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                            Demo-/Systemkonten — {preview.demo_total} Fahrzeuge (werden komplett gelöscht)
                        </div>
                        <ul className="text-xs text-gray-700 space-y-0.5" data-testid="cleanup-demo-groups">
                            {(preview.demo_groups || []).map((g) => (
                                <li key={g.owner_id} className="flex justify-between border-b border-gray-100 py-1">
                                    <span className="truncate">{g.company_name || "—"} <span className="text-gray-400">({g.email})</span></span>
                                    <span className="font-mono-num font-bold shrink-0">{g.count}</span>
                                </li>
                            ))}
                            {(preview.demo_groups || []).length === 0 && <li className="text-gray-400">Keine Demo-Fahrzeuge vorhanden.</li>}
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                            Verdachtsfälle echter Händler (Test-Titel oder ohne Fahrzeugnummern) — bitte einzeln bestätigen
                        </div>
                        <ul className="text-xs text-gray-700 space-y-0.5" data-testid="cleanup-dealer-candidates">
                            {(preview.dealer_candidates || []).map((c) => (
                                <li key={c.id} className="flex items-center gap-2 border-b border-gray-100 py-1">
                                    <input type="checkbox" checked={!!checked[c.id]}
                                        onChange={(e) => setChecked((s) => ({ ...s, [c.id]: e.target.checked }))}
                                        data-testid={`cleanup-cand-${c.id}`} />
                                    <span className="truncate flex-1">{c.title} <span className="text-gray-400">· {c.owner_company || c.owner_email} · {c.status}</span></span>
                                    {c.reason && <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 ${c.reason === "Test-Titel" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} title={c.reason}>{c.reason === "Test-Titel" ? "Test-Titel" : "Ohne Fahrzeugnr."}</span>}
                                </li>
                            ))}
                            {(preview.dealer_candidates || []).length === 0 && <li className="text-gray-400">Keine Verdachtsfälle.</li>}
                        </ul>
                    </div>
                    <Button onClick={run} disabled={busy || (preview.demo_total === 0 && Object.values(checked).every((v) => !v))}
                        className="bg-red-700 hover:bg-red-800 text-white rounded-sm font-bold" data-testid="cleanup-run-btn">
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                        Endgültig löschen
                    </Button>
                </div>
            )}
        </div>
    );
};
